/**
 * Partes da página do cadastro de produto (padrão do `pedidos/detailParts.jsx`):
 * cabeçalho fixo, identidade com preço/custo/margem, navegação lateral com
 * scroll-spy, casca das seções, selo de vínculo com o Bling, lista de
 * pendências, dica "Usar valor do Bling" e o modal de marca nova.
 *
 * Tudo em tokens do DS (`mono-*` + success/warning/danger/info com `dark:`):
 * o `.dc.html` do design é o app no tema escuro — os hex dele não entram.
 */
import { useState } from 'react';
import { ArrowLeft, Info, Package, Warning } from 'shared/icons';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { MONO_CLASS } from '../../components/ui/Mono';
import Switch from '../../components/ui/Switch';
import {
    BLING_LINK_STATUS_META,
    BRAND_NAME_MAX,
    COST_PRICE_DECIMALS,
    PRODUCT_FORMAT_LABELS,
    PRODUCT_ORIGIN_LABELS,
    PRODUCT_STATUS_META,
} from '../../constants/productCatalog';
import api from '../../services/api';
import { BrandMatchKey, NormalizeBrandName } from '../../utils/brands';
import { FormatDateTimeBr } from '../../utils/date';
import { FormatDecimal1, FormatDecimalBr, FormatMoney } from '../../utils/format';
import { SecureMlImageUrl } from '../../utils/ml';
import { ComputePriceKpis, FieldDomId, SectionDomId, UpperCollapse } from './productForm';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// ── Selos ────────────────────────────────────────────────────────────────────

/** Vínculo com o Bling (`bling_status` do servidor; o front não calcula). */
export const BlingLinkBadge = ({ status, blingId = null, compact = false }) => {
    const meta = BLING_LINK_STATUS_META[status];
    if (!meta) {
        return <span className="text-mono-400">—</span>;
    }
    const title = [meta.help, blingId ? `Bling #${blingId}` : null].filter(Boolean).join(' · ');
    return (
        <span title={title} className="inline-flex">
            <Badge variant={meta.variant} className="whitespace-nowrap">
                {compact ? meta.label : `Bling: ${meta.label}`}
            </Badge>
        </span>
    );
};

/** Situação com ponto colorido (lista e identidade). */
export const ProductStatusDot = ({ status }) => {
    const meta = PRODUCT_STATUS_META[status] || PRODUCT_STATUS_META.ACTIVE;
    const active = status !== 'INACTIVE';
    return (
        <span
            className={cx(
                'inline-flex items-center gap-1.5 whitespace-nowrap text-sm',
                active ? 'text-success-700 dark:text-success-400' : 'text-mono-500 dark:text-mono-400',
            )}
        >
            <span
                aria-hidden="true"
                className={cx('h-1.5 w-1.5 rounded-full', active ? 'bg-success-500' : 'bg-mono-400 dark:bg-mono-500')}
            />
            {meta.label}
        </span>
    );
};

// ── Cabeçalho fixo ───────────────────────────────────────────────────────────

/**
 * Barra fixa no topo da janela: voltar / título à esquerda; alteração
 * pendente, "Alterado em", situação e Descartar/Salvar à direita. Sem
 * `canEdit`, some o Salvar/Descartar e o switch fica travado.
 */
export const EditorHeader = ({
    mode,
    title,
    onBack,
    updatedAt,
    status,
    onToggleStatus,
    canEdit,
    isDirty,
    dirtyCount,
    saving,
    onDiscard,
    onSave,
}) => (
    <div className="sticky top-0 z-20 -mx-4 border-b border-mono-200 bg-mono-50/95 px-4 py-3 backdrop-blur md:-mx-6 md:px-6 lg:-mx-8 lg:px-8 dark:border-mono-800 dark:bg-mono-950/95">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2 text-sm">
                <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg border border-mono-300 px-2.5 py-1 text-mono-700 transition-colors hover:bg-mono-100 dark:border-mono-700 dark:text-mono-300 dark:hover:bg-mono-900"
                >
                    <ArrowLeft size={14} /> Produtos
                </button>
                <span className="text-mono-400" aria-hidden="true">
                    /
                </span>
                <span className="truncate text-mono-700 dark:text-mono-300" title={title}>
                    {title}
                </span>
            </div>
            <div className="flex flex-shrink-0 flex-wrap items-center gap-3">
                {isDirty ? (
                    <span className="text-xs font-medium text-warning-700 dark:text-warning-300" aria-live="polite">
                        {mode === 'new'
                            ? 'Não salvo'
                            : `Alterações não salvas${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
                    </span>
                ) : (
                    <span className="text-xs text-mono-500 dark:text-mono-400">
                        {mode === 'new' ? 'Não salvo' : `Alterado em ${FormatDateTimeBr(updatedAt)}`}
                    </span>
                )}
                <div className="flex items-center rounded-lg border border-mono-300 px-2.5 py-1.5 dark:border-mono-700">
                    <Switch
                        id="produto-campo-status"
                        checked={status !== 'INACTIVE'}
                        onCheckedChange={(on) => onToggleStatus(on ? 'ACTIVE' : 'INACTIVE')}
                        disabled={!canEdit || saving}
                        label={status !== 'INACTIVE' ? 'Ativo' : 'Inativo'}
                        title="Situação do produto (grava com o Salvar)"
                    />
                </div>
                {canEdit ? (
                    <>
                        <Button variant="secondary" size="sm" onClick={onDiscard} disabled={saving || !isDirty}>
                            Descartar
                        </Button>
                        <Button size="sm" onClick={onSave} loading={saving} disabled={mode !== 'new' && !isDirty}>
                            {mode === 'new' ? 'Cadastrar' : 'Salvar'}
                        </Button>
                    </>
                ) : null}
            </div>
        </div>
    </div>
);

// ── Identidade ───────────────────────────────────────────────────────────────

const Thumbnail = ({ url, title }) => {
    const [failed, setFailed] = useState(false);
    const src = SecureMlImageUrl(url);
    if (src && !failed) {
        return (
            <img
                src={src}
                alt={title || 'Produto'}
                onError={() => setFailed(true)}
                className="h-24 w-24 flex-shrink-0 rounded-xl border border-mono-200 bg-white object-contain dark:border-mono-800"
            />
        );
    }
    return (
        <div className="flex h-24 w-24 flex-shrink-0 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-mono-300 bg-mono-100 text-center text-xs text-mono-500 dark:border-mono-700 dark:bg-mono-900 dark:text-mono-400">
            <Package size={22} aria-hidden="true" />
            Sem imagem
        </div>
    );
};

const Kpi = ({ label, value, title, tone = null }) => (
    <div className="flex min-w-[7.5rem] flex-col gap-1 px-4 py-3" title={title}>
        <span className="text-xs text-mono-500 dark:text-mono-400">{label}</span>
        <span
            className={cx(
                MONO_CLASS,
                'text-base',
                tone === 'positive'
                    ? 'text-success-700 dark:text-success-400'
                    : tone === 'negative'
                      ? 'text-danger-600 dark:text-danger-400'
                      : 'text-black dark:text-white',
            )}
        >
            {value}
        </span>
    </div>
);

/**
 * Miniatura (1º anúncio do SKU), selos (formato, origem, Bling, situação),
 * nome, linha monoespaçada SKU/GTIN/NCM e o bloco preço/custo/margem —
 * calculado do RASCUNHO, ao vivo.
 */
export const ProductIdentity = ({ mode, product, form }) => {
    const kpis = ComputePriceKpis(form);
    const title = UpperCollapse(form.title) || (mode === 'new' ? 'Novo produto' : product?.title || '—');
    const origin = mode === 'new' ? 'OWN' : product?.origin;
    const skuShown = mode === 'new' ? UpperCollapse(form.sku) : product?.sku_display || product?.sku;
    const margin =
        kpis.margin === null
            ? { value: '—', tone: null }
            : { value: `${FormatDecimal1(kpis.margin)}%`, tone: kpis.margin >= 0 ? 'positive' : 'negative' };

    return (
        <div className="flex flex-wrap items-start gap-6">
            <Thumbnail url={product?.thumbnail_url} title={product?.title} />
            <div className="flex min-w-[16rem] flex-1 flex-col gap-3">
                <div className="flex flex-wrap gap-2">
                    <Badge variant={form.format === 'KIT' ? 'info' : 'default'}>
                        {PRODUCT_FORMAT_LABELS[form.format] || PRODUCT_FORMAT_LABELS.SIMPLE}
                    </Badge>
                    {origin ? (
                        <Badge variant="neutral">
                            {PRODUCT_ORIGIN_LABELS[origin] || origin}
                            {product?.bling_id ? ` · #${product.bling_id}` : ''}
                        </Badge>
                    ) : null}
                    {mode === 'new' ? (
                        <BlingLinkBadge status="NOT_LINKED" />
                    ) : (
                        <BlingLinkBadge status={product?.bling_status} blingId={product?.bling_id} />
                    )}
                </div>
                <h1 className="text-2xl font-semibold leading-tight text-black dark:text-white">{title}</h1>
                <div
                    className={cx(
                        MONO_CLASS,
                        'flex flex-wrap gap-x-5 gap-y-1 text-sm text-mono-500 dark:text-mono-400',
                    )}
                >
                    <span>
                        SKU <span className="text-black dark:text-white">{skuShown || '—'}</span>
                    </span>
                    <span>
                        GTIN <span className="text-black dark:text-white">{form.ean || 'sem GTIN'}</span>
                    </span>
                    <span>
                        NCM <span className="text-black dark:text-white">{form.ncm || '—'}</span>
                    </span>
                </div>
            </div>
            <div className="grid grid-cols-3 divide-x divide-mono-200 rounded-xl border border-mono-200 bg-white dark:divide-mono-800 dark:border-mono-800 dark:bg-mono-950">
                <Kpi label="Preço venda" value={FormatMoney(kpis.price)} />
                <Kpi
                    label="Custo"
                    value={FormatMoney(kpis.cost)}
                    title={
                        kpis.cost === null
                            ? 'Custo pendente'
                            : `Custo por unidade: R$ ${FormatDecimalBr(kpis.cost, { minFractionDigits: 2, maxFractionDigits: COST_PRICE_DECIMALS })}`
                    }
                />
                <Kpi
                    label="Margem (referência)"
                    value={margin.value}
                    tone={margin.tone}
                    title="(preço − custo) ÷ preço, sobre o preço de venda deste cadastro"
                />
            </div>
        </div>
    );
};

// ── Navegação lateral ────────────────────────────────────────────────────────

/**
 * `nav` fixa ao rolar (lg+); abaixo de lg vira uma faixa horizontal rolável.
 * Barra de 2 px no item ativo; ponto vermelho com erro, âmbar com pendência.
 */
export const SectionNav = ({ sections, activeId, onSelect }) => (
    <nav
        aria-label="Seções do cadastro"
        className="flex gap-1 overflow-x-auto pb-1 lg:sticky lg:top-24 lg:flex-col lg:self-start lg:overflow-visible lg:pb-0"
    >
        {sections.map((section) => {
            const active = section.id === activeId;
            return (
                <button
                    key={section.id}
                    type="button"
                    onClick={() => onSelect(section.id)}
                    aria-current={active ? 'true' : undefined}
                    className={cx(
                        'flex h-8 flex-shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg px-2.5 text-left text-sm transition-colors',
                        active
                            ? 'bg-mono-100 font-medium text-black dark:bg-mono-900 dark:text-white'
                            : 'text-mono-600 hover:text-black dark:text-mono-400 dark:hover:text-white',
                    )}
                >
                    <span
                        aria-hidden="true"
                        className={cx('h-3.5 w-0.5 rounded-full', active ? 'bg-black dark:bg-white' : 'bg-transparent')}
                    />
                    <span className="flex-1">{section.label}</span>
                    {section.errorCount > 0 ? (
                        <span
                            className="h-1.5 w-1.5 rounded-full bg-danger-500"
                            title={`${section.errorCount} campo(s) com erro`}
                        />
                    ) : section.hasPending ? (
                        <span className="h-1.5 w-1.5 rounded-full bg-warning-500" title="Pendência nesta seção" />
                    ) : null}
                </button>
            );
        })}
    </nav>
);

// ── Seção ────────────────────────────────────────────────────────────────────

/** Casca de uma seção: Card com âncora do scroll-spy, título, descrição e grid de 6. */
export const FormSection = ({ id, title, description = null, notice = null, children }) => (
    <Card id={SectionDomId(id)} className="flex scroll-mt-24 flex-col gap-5">
        <div className="flex flex-col gap-1">
            <h2 className="text-base font-semibold text-black dark:text-white">{title}</h2>
            {description ? <p className="text-sm text-mono-500 dark:text-mono-400">{description}</p> : null}
        </div>
        {notice}
        {children ? <div className="grid grid-cols-6 gap-x-4 gap-y-5">{children}</div> : null}
    </Card>
);

/**
 * Props de um `Input`/`Select` ligado ao rascunho. `ctx` é o contexto que o
 * editor passa às seções: `{ form, errors, warnings, hints, setField, readOnly }`.
 * Precedência no rodapé do campo: erro → aviso → dica (pendência `info` do
 * servidor vence a dica fixa da seção).
 */
export function FieldInputProps(ctx, field, { hint = null } = {}) {
    return {
        id: FieldDomId(field),
        value: ctx.form[field] ?? '',
        onChange: (event) => ctx.setField(field, event.target.value),
        errorMessage: ctx.errors[field] || undefined,
        warning: ctx.warnings[field] || undefined,
        hint: ctx.hints?.[field] || hint || undefined,
        disabled: ctx.readOnly,
    };
}

/** Célula do grid de 6 colunas (no celular, sempre a linha inteira). */
export const FieldCell = ({ span = 6, children }) => {
    const spans = {
        2: 'col-span-6 md:col-span-2',
        3: 'col-span-6 md:col-span-3',
        4: 'col-span-6 md:col-span-4',
        6: 'col-span-6',
    };
    return <div className={spans[span] || spans[6]}>{children}</div>;
};

/** Aviso dentro de uma seção (informativo ou pendência). */
export const SectionNotice = ({ tone = 'info', children }) => {
    const tones = {
        info: 'border-info-border bg-info-soft text-info-text dark:border-info-900/60 dark:bg-info-900/20 dark:text-info-300',
        warning:
            'border-warning-border bg-warning-soft text-warning-text dark:border-warning-900/60 dark:bg-warning-900/20 dark:text-warning-300',
    };
    const Icon = tone === 'warning' ? Warning : Info;
    return (
        <div className={cx('flex items-start gap-2 rounded-lg border px-3 py-2 text-sm', tones[tone] || tones.info)}>
            <Icon size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div className="min-w-0">{children}</div>
        </div>
    );
};

/** Valor só de leitura com o mesmo desenho de um campo (SKU na edição, saldo). */
export const ReadOnlyField = ({ id, label, value, mono = false, hint = null, warning = null }) => (
    <div className="w-full">
        <span
            id={id ? `${id}-label` : undefined}
            className="mb-1 block text-sm font-medium text-mono-700 dark:text-mono-300"
        >
            {label}
        </span>
        <div
            id={id}
            className={cx(
                'flex min-h-[42px] items-center rounded-lg border border-mono-200 bg-mono-100 px-4 py-2 text-base text-black dark:border-mono-800 dark:bg-mono-900 dark:text-white',
                mono ? MONO_CLASS : '',
            )}
        >
            {value}
        </div>
        {warning ? (
            <p aria-live="polite" className="mt-1 text-sm text-warning-700 dark:text-warning-300">
                {warning}
            </p>
        ) : hint ? (
            <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">{hint}</p>
        ) : null}
    </div>
);

// ── Dicas do Bling ───────────────────────────────────────────────────────────

/**
 * "No Bling em dd/mm/aaaa: custo do fornecedor R$ X  [Usar valor do Bling]".
 * O botão só PREENCHE o campo do rascunho (fica alterado; grava com o Salvar).
 * `suggestion.value === null` = só informa (ex.: marca fora da lista).
 */
export const BlingSuggestion = ({ suggestion, capturedAt, onUse, disabled = false }) => {
    if (!suggestion) {
        return null;
    }
    const when = capturedAt ? ` em ${FormatDateTimeBr(capturedAt).split(',')[0]}` : '';
    return (
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-mono-500 dark:text-mono-400">
            <span>
                No Bling{when}: {suggestion.label}{' '}
                <span className={cx(MONO_CLASS, 'text-mono-700 dark:text-mono-300')}>{suggestion.display}</span>
            </span>
            {suggestion.value !== null && !disabled ? (
                <button
                    type="button"
                    onClick={() => onUse(suggestion.value)}
                    className="font-medium text-link-600 hover:underline dark:text-link-400"
                >
                    Usar valor do Bling
                </button>
            ) : null}
        </p>
    );
};

// ── Pendências ───────────────────────────────────────────────────────────────

const SEVERITY_DOT = {
    blocking: 'bg-danger-500',
    warning: 'bg-warning-500',
    info: 'bg-info-500',
};

/**
 * Pendências calculadas pelo servidor (`pending[]` do detalhe). Cada uma leva
 * à seção; o aviso também aparece no próprio campo enquanto ele não mudar.
 */
export const PendingList = ({ pending, sectionLabels, onGo }) => {
    const items = Array.isArray(pending) ? pending : [];
    if (items.length === 0) {
        return null;
    }
    return (
        <Card className="flex flex-col gap-3 !p-4">
            <h2 className="text-sm font-semibold text-black dark:text-white">
                Pendências do cadastro <span className="font-normal text-mono-500">({items.length})</span>
            </h2>
            <ul className="flex flex-col divide-y divide-mono-200 dark:divide-mono-800">
                {items.map((item) => (
                    <li key={item.code} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                        <span
                            aria-hidden="true"
                            className={cx(
                                'h-2 w-2 flex-shrink-0 rounded-full',
                                SEVERITY_DOT[item.severity] || SEVERITY_DOT.info,
                            )}
                        />
                        <span className="font-medium text-black dark:text-white">{item.label || item.code}</span>
                        {item.message ? <span className="text-mono-600 dark:text-mono-400">{item.message}</span> : null}
                        {item.section && sectionLabels[item.section] ? (
                            <button
                                type="button"
                                onClick={() => onGo(item.section)}
                                className="ml-auto text-xs font-medium text-link-600 hover:underline dark:text-link-400"
                            >
                                {sectionLabels[item.section]}
                            </button>
                        ) : null}
                    </li>
                ))}
            </ul>
        </Card>
    );
};

// ── Marca nova ───────────────────────────────────────────────────────────────

/**
 * Cria a marca como org SUPPLIER (`organizations:write`) com a normalização do
 * servidor (maiúsculas pt-BR, espaços colapsados, até 45). Duplicata sem acento
 * é avisada antes (o servidor recusa com 409 BRAND_ALREADY_EXISTS).
 */
export const NewBrandModal = ({ isOpen, onClose, brands = [], onCreated }) => {
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const normalized = NormalizeBrandName(name);
    const duplicate = normalized
        ? brands.find((item) => item?.brand && BrandMatchKey(item.brand) === BrandMatchKey(normalized))
        : null;
    const localError =
        normalized.length > BRAND_NAME_MAX
            ? `Até ${BRAND_NAME_MAX} caracteres`
            : normalized && normalized.length < 2
              ? 'Mínimo de 2 caracteres'
              : null;

    const close = () => {
        if (saving) {
            return;
        }
        setName('');
        setError(null);
        onClose();
    };

    const submit = async (event) => {
        event?.preventDefault?.();
        if (!normalized || localError) {
            setError(localError || 'Informe a marca');
            return;
        }
        if (duplicate) {
            onCreated(duplicate.brand);
            close();
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const created = await api.createOrganization({
                name: normalized,
                type: 'SUPPLIER',
                assigned_brand: normalized,
            });
            setSaving(false);
            setName('');
            onCreated(created?.assigned_brand || normalized);
            onClose();
        } catch (err) {
            setSaving(false);
            setError(err?.message || 'Não foi possível criar a marca');
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={close}
            title="Nova marca"
            size="md"
            actions={
                <>
                    <Button variant="secondary" onClick={close} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button onClick={submit} loading={saving} disabled={!normalized || Boolean(localError)}>
                        {duplicate ? 'Usar a existente' : 'Criar marca'}
                    </Button>
                </>
            }
        >
            <form onSubmit={submit} className="flex flex-col gap-3">
                <p className="text-sm text-mono-600 dark:text-mono-400">
                    A marca vira um fornecedor (organização SUPPLIER) e passa a valer na lista de todos os produtos.
                </p>
                <Input
                    id="nova-marca-nome"
                    label="Marca"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    maxLength={BRAND_NAME_MAX + 10}
                    autoComplete="off"
                    autoFocus
                    className="uppercase"
                    errorMessage={error || localError || undefined}
                    warning={duplicate ? `Já existe: ${duplicate.brand}` : undefined}
                    hint={normalized ? `Grava como ${normalized}` : `Até ${BRAND_NAME_MAX} caracteres, em maiúsculas`}
                    labelAside={`${normalized.length}/${BRAND_NAME_MAX}`}
                />
            </form>
        </Modal>
    );
};
